/**
 * DOM mirror of PdfLayoutBands — same DocumentLayoutV1 coordinates (as CSS pt),
 * same PdfLayoutBandData, same labeledFieldPresentation helpers.
 * Used by OfferDocumentEditor when a default layout template is active.
 */
import React from 'react';
import type { DocumentLayoutV1, LayoutElement } from '../../settings/utils/documentLayoutTypes';
import { lineThicknessPt, resolveLineOrientation } from '../../settings/utils/documentLayoutTypes';
import {
  DOCUMENT_FOLD_MARK_COLOR,
  layoutShowsFoldMarks,
  resolveDocumentFoldMarkRects,
} from '../../settings/utils/documentFoldMarks';
import { companyContactFieldLines } from '../utils/offerDocumentUtils';
import { PDF_A4_WIDTH_PT } from '../../settings/utils/documentLayoutTypes';
import { isInvoiceFamilyDocumentType } from '../../../utils/documentNumberFormat';
import {
  DEFAULT_FIELD_LABELS,
  documentNumberFieldLabel,
  inlineLabelValueSeparator,
  issueDateFieldLabel,
  labeledFieldPresentation,
  leistungszeitraumFieldLabel,
  parseFreeTextWithTokens,
  formatOfferTitleLine,
  layoutAlignToFlexJustify,
  resolveLayoutTextAlign,
  layoutAnsprechHeading,
  type LayoutTokenValues,
  type LabeledFieldPresentation,
} from '../../settings/utils/layoutFieldPresentation';
import type { PdfLayoutBandData } from './PdfLayoutBands';

/** The embedded PDF font (@font-face in index.css), then Helvetica-metric fallbacks. */
export const OFFER_PDF_METRIC_FONT =
  "'TeX Gyre Heros', Arial, 'Liberation Sans', 'Nimbus Sans', Helvetica, sans-serif";

/**
 * Document preview font. The PDF embeds TeX Gyre Heros and draws its outlines.
 * `font-size-adjust: none` opts out of the app-wide Inter correction (0.5).
 * `geometricPrecision` skips ClearType hinting, which otherwise redraws the
 * same file so the sheet no longer matches the PDF at 9.5pt.
 */
export const OFFER_PDF_FONT_STYLE = {
  fontFamily: OFFER_PDF_METRIC_FONT,
  fontSizeAdjust: 'none',
  fontSynthesis: 'none',
  textRendering: 'geometricPrecision',
  WebkitFontSmoothing: 'auto',
} as const satisfies React.CSSProperties;

const BAND_LINE_HEIGHT = 1.35;

function bandTokenValues(data: PdfLayoutBandData): LayoutTokenValues {
  const invoiceNumber =
    data.invoiceNumberLabel?.trim() ||
    (data.documentTypeLabel
      ? data.documentHeadingLine1.replace(new RegExp(`^${data.documentTypeLabel}\\s+`), '').trim()
      : '') ||
    data.documentHeadingLine1.trim();
  const docType = (data.documentTypeLabel ?? '').trim();
  return {
    leistungsort: data.leistungsort,
    leistungszeitraum: data.leistungszeitraumDisplay,
    leistungszeitraumLabel: leistungszeitraumFieldLabel(docType),
    issueDate: data.todayLabel,
    issueDateLabel: issueDateFieldLabel(docType),
    dueDate: data.dueDateLabel ?? '',
    customerNumber: data.customerNumberLabel ?? '',
    projectNumber: data.projectNumberLabel ?? '',
    orderNumber: data.orderNumberLabel ?? '',
    // HTML editor: show the label on invoices even before a value is entered.
    orderNumberLabel: isInvoiceFamilyDocumentType(docType) ? DEFAULT_FIELD_LABELS.orderNumber : '',
    orderTotal: data.orderTotalLabel ?? '',
    invoiceNumber,
    invoiceNumberLabel: documentNumberFieldLabel(docType),
    documentType: docType,
    documentTitle: data.documentHeadingLine1,
    offerTitle: data.offerTitleRest,
  };
}

function elBox(el: LayoutElement): React.CSSProperties {
  const isFixedMedia = ['logo', 'headerBanner', 'signatureImage', 'divider', 'colorBlock'].includes(
    el.key,
  );
  return {
    position: 'absolute',
    left: `${el.xPt}pt`,
    top: `${el.yPt}pt`,
    width: `${el.wPt}pt`,
    ...(isFixedMedia ? { height: `${el.hPt}pt` } : { minHeight: `${el.hPt}pt` }),
    zIndex: el.z ?? 0,
    boxSizing: 'border-box',
  };
}

function textCss(el: LayoutElement): React.CSSProperties {
  return {
    fontSize: `${el.fontSizePt ?? 9.5}pt`,
    ...OFFER_PDF_FONT_STYLE,
    fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? 'italic' : 'normal',
    textDecoration: el.underline ? 'underline' : 'none',
    // The PDF underline runs through every glyph; without this the browser skips
    // it around characters like "|" and preview and export drift apart.
    textDecorationSkipInk: 'none',
    color: el.color || '#334155',
    textAlign: resolveLayoutTextAlign(el.align),
    lineHeight: BAND_LINE_HEIGHT,
    margin: 0,
    padding: 0,
  };
}

function labelCss(pres: LabeledFieldPresentation): React.CSSProperties {
  return {
    fontSize: `${pres.labelFontSizePt}pt`,
    ...OFFER_PDF_FONT_STYLE,
    fontWeight: pres.labelBold ? 700 : 400,
    fontStyle: pres.italic ? 'italic' : 'normal',
    textDecoration: pres.underline ? 'underline' : 'none',
    textDecorationSkipInk: 'none',
    color: pres.labelColor,
    textAlign: resolveLayoutTextAlign(pres.align),
    lineHeight: BAND_LINE_HEIGHT,
  };
}

function valueCss(pres: LabeledFieldPresentation): React.CSSProperties {
  return {
    fontSize: `${pres.valueFontSizePt}pt`,
    ...OFFER_PDF_FONT_STYLE,
    fontWeight: pres.valueBold ? 700 : 400,
    fontStyle: pres.italic ? 'italic' : 'normal',
    textDecoration: pres.underline ? 'underline' : 'none',
    textDecorationSkipInk: 'none',
    color: pres.valueColor,
    textAlign: resolveLayoutTextAlign(pres.align),
    lineHeight: BAND_LINE_HEIGHT,
  };
}

function renderLabeledField(
  el: LayoutElement,
  value: string,
  opts?: { defaultLabel?: string },
): React.ReactNode {
  const pres = labeledFieldPresentation(el, value, opts);
  const box = elBox(el);
  if (pres.placement === 'inline') {
    const sep = inlineLabelValueSeparator(el.key);
    return (
      <div
        key={el.id}
        style={{
          ...box,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: layoutAlignToFlexJustify(el.align),
          textAlign: resolveLayoutTextAlign(el.align),
        }}
      >
        {pres.label ? (
          <span style={labelCss(pres)}>
            {pres.label}
            {sep}
          </span>
        ) : null}
        <span style={valueCss(pres)}>{pres.value}</span>
      </div>
    );
  }
  return (
    <div key={el.id} style={{ ...box, textAlign: resolveLayoutTextAlign(el.align) }}>
      {pres.label ? (
        <div style={{ ...labelCss(pres), marginBottom: 2 }}>{pres.label}</div>
      ) : null}
      <div style={valueCss(pres)}>{pres.value}</div>
    </div>
  );
}

export type HtmlBandRenderOverride = (el: LayoutElement) => React.ReactNode | undefined;

function wrapOverride(
  el: LayoutElement,
  override: HtmlBandRenderOverride | undefined,
  fallback: () => React.ReactNode,
): React.ReactNode {
  if (override) {
    const custom = override(el);
    if (custom !== undefined) {
      return (
        <div key={el.id} style={elBox(el)}>
          {custom}
        </div>
      );
    }
  }
  return fallback();
}

function renderHeaderElement(
  el: LayoutElement,
  data: PdfLayoutBandData,
  override?: HtmlBandRenderOverride,
): React.ReactNode {
  const style = textCss(el);
  const box = elBox(el);

  return wrapOverride(el, override, () => {
    switch (el.key) {
      case 'logo':
        return data.logoUrl ? (
          <div key={el.id} style={box}>
            <img
              src={data.logoUrl}
              alt="Firmenlogo"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'right center',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          </div>
        ) : null;
      case 'headerBanner': {
        const src = data.assetUrls?.[el.id] ?? null;
        if (!src) return null;
        return (
          <div key={el.id} style={box}>
            <img
              src={src}
              alt="Bild"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                pointerEvents: 'none',
                filter: el.imageGrayscale ? 'grayscale(100%)' : undefined,
              }}
            />
          </div>
        );
      }
      case 'senderHeadline': {
        const companyName = (data.company?.company_name ?? '').trim() || 'Firma';
        const headline = [companyName, ...data.companyLines].filter(Boolean).join(' | ');
        return (
          <div key={el.id} style={box}>
            <p style={style}>{headline}</p>
          </div>
        );
      }
      case 'companyAddress':
      case 'companyPhone':
      case 'companyEmail':
      case 'companyWebsite': {
        const lines = companyContactFieldLines(el.key, data.company, {
          websiteDisplay: data.websiteDisplay,
        });
        if (lines.length === 0) return null;
        const href =
          el.key === 'companyEmail' && lines[0]
            ? `mailto:${lines[0]}`
            : el.key === 'companyWebsite'
              ? data.websiteHref
              : null;
        return (
          <div key={el.id} style={box}>
            {lines.map((ln, i) => (
              <div key={`${el.key}-${i}`} style={{ ...style, marginBottom: i < lines.length - 1 ? 2 : 0 }}>
                {href && i === 0 ? (
                  <a href={href} target={el.key === 'companyWebsite' ? '_blank' : undefined} rel="noreferrer" style={{ color: style.color, textDecoration: 'none' }}>
                    {ln}
                  </a>
                ) : (
                  ln
                )}
              </div>
            ))}
          </div>
        );
      }
      case 'recipient':
        return (
          <div key={el.id} style={box}>
            {data.recipientNameLines.map((line, i) => (
              <div key={`rn-${i}`} style={{ ...style, marginBottom: 2 }}>
                {line}
              </div>
            ))}
            {data.recipientStreetLine ? (
              <div style={{ ...style, marginBottom: 2 }}>{data.recipientStreetLine}</div>
            ) : null}
            {data.recipientPlzOrtLine ? (
              <div style={style}>{data.recipientPlzOrtLine}</div>
            ) : null}
          </div>
        );
      case 'ansprechpartner':
        if (!data.showAnsprechBlock) return null;
        return (
          <div key={el.id} style={box}>
            <div style={{ ...style, fontWeight: 700, marginBottom: 2 }}>
              {layoutAnsprechHeading(data)}
            </div>
            <div style={style}>{data.ansprechLine}</div>
            {data.ansprechMail ? (
              <div style={style}>
                <a
                  href={`mailto:${data.ansprechMail}`}
                  style={{ color: style.color, textDecoration: 'none' }}
                >
                  {data.ansprechMail}
                </a>
              </div>
            ) : null}
          </div>
        );
      case 'documentTitle': {
        const number =
          data.invoiceNumberLabel?.trim() ||
          (data.documentTypeLabel
            ? data.documentHeadingLine1
                .replace(new RegExp(`^${data.documentTypeLabel}\\s+`), '')
                .trim()
            : '') ||
          data.documentHeadingLine1.trim() ||
          '—';
        return renderLabeledField(el, number, {
          defaultLabel: (data.documentTypeLabel ?? '').trim() || 'Rechnung',
        });
      }
      case 'documentType':
        return (
          <div key={el.id} style={box}>
            <p style={style}>{(data.documentTypeLabel ?? '').trim() || 'Rechnung'}</p>
          </div>
        );
      case 'offerTitle':
        return (
          <div key={el.id} style={box}>
            <p style={style}>
              {formatOfferTitleLine(data.documentTypeLabel, data.offerTitleRest, {
                hidePrefix: data.hideDocumentTypeInTitle,
              })}
            </p>
          </div>
        );
      case 'invoiceNumber':
        return renderLabeledField(
          el,
          data.invoiceNumberLabel?.trim() || data.documentHeadingLine1.trim() || '—',
          { defaultLabel: documentNumberFieldLabel(data.documentTypeLabel) },
        );
      case 'issueDate':
        return renderLabeledField(el, data.todayLabel || '—', {
          defaultLabel: issueDateFieldLabel(data.documentTypeLabel),
        });
      case 'dueDate':
        if (!data.dueDateLabel?.trim()) return null;
        return renderLabeledField(el, data.dueDateLabel);
      case 'customerNumber':
        return renderLabeledField(el, data.customerNumberLabel?.trim() || '—');
      case 'projectNumber':
        if (!data.projectNumberLabel?.trim()) return null;
        return renderLabeledField(el, data.projectNumberLabel);
      case 'orderNumber':
        if (!data.orderNumberLabel?.trim()) return null;
        return renderLabeledField(el, data.orderNumberLabel);
      case 'leistungsort':
        return renderLabeledField(el, data.leistungsort.trim() || '—');
      case 'leistungszeitraum': {
        if (!data.leistungszeitraumDisplay.trim()) return null;
        const lzLabel = leistungszeitraumFieldLabel(data.documentTypeLabel);
        if (!lzLabel) return null;
        return renderLabeledField(el, data.leistungszeitraumDisplay, { defaultLabel: lzLabel });
      }
      case 'freeText': {
        const segments = parseFreeTextWithTokens(el.text?.trim() || '', bandTokenValues(data));
        return (
          <div key={el.id} style={box}>
            <p style={style}>
              {segments.map((seg, i) => (
                <span
                  key={i}
                  style={{ fontWeight: seg.boldValue || el.bold ? 700 : style.fontWeight }}
                >
                  {seg.text}
                </span>
              ))}
            </p>
          </div>
        );
      }
      case 'divider':
        return (
          <div
            key={el.id}
            style={{
              ...box,
              backgroundColor: el.color || '#111827',
              ...(resolveLineOrientation(el) === 'vertical'
                ? { width: `${lineThicknessPt(el)}pt` }
                : { height: `${lineThicknessPt(el)}pt` }),
            }}
          />
        );
      case 'colorBlock':
        return (
          <div
            key={el.id}
            style={{
              ...box,
              backgroundColor: el.bg || '#eff6ff',
              borderRadius: el.radiusPt ?? 0,
              border: el.border ? '1px solid #9ca3af' : undefined,
            }}
          />
        );
      default:
        return null;
    }
  });
}

function renderFooterElement(
  el: LayoutElement,
  data: PdfLayoutBandData,
  override?: HtmlBandRenderOverride,
): React.ReactNode {
  const style = textCss(el);
  const box = elBox(el);
  const company = data.company;

  return wrapOverride(el, override, () => {
    switch (el.key) {
      case 'legalBlock':
        return (
          <div key={el.id} style={box}>
            {data.directorLines.map((ln, i) =>
              (ln ?? '').trim() ? (
                <div key={`dl-${i}`} style={style}>
                  {ln}
                </div>
              ) : null,
            )}
            {data.registryLine?.trim() ? (
              <div style={style}>{data.registryLine}</div>
            ) : null}
            {(company?.vat_id ?? '').trim() ? (
              <div style={style}>USt-ID {(company?.vat_id ?? '').trim()}</div>
            ) : null}
            {(company?.tax_number ?? '').trim() ? (
              <div style={style}>Steuernummer {(company?.tax_number ?? '').trim()}</div>
            ) : null}
          </div>
        );
      case 'bankBlock':
        return (
          <div key={el.id} style={box}>
            {(company?.bank_name ?? '').trim() ? (
              <div style={style}>{(company?.bank_name ?? '').trim()}</div>
            ) : null}
            {(company?.bank_account_holder ?? '').trim() ? (
              <div style={style}>{(company?.bank_account_holder ?? '').trim()}</div>
            ) : null}
            {(company?.iban ?? '').trim() ? (
              <div style={style}>IBAN {(company?.iban ?? '').trim()}</div>
            ) : null}
            {(company?.bic ?? '').trim() ? (
              <div style={style}>BIC {(company?.bic ?? '').trim()}</div>
            ) : null}
          </div>
        );
      case 'contactBlock':
        return (
          <div key={el.id} style={box}>
            {data.websiteDisplay?.trim() ? (
              <div style={style}>
                {data.websiteHref ? (
                  <a
                    href={data.websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: style.color, textDecoration: 'none' }}
                  >
                    {data.websiteDisplay}
                  </a>
                ) : (
                  data.websiteDisplay
                )}
              </div>
            ) : null}
            {(company?.phone ?? '').trim() ? (
              <div style={style}>Telefon {(company?.phone ?? '').trim()}</div>
            ) : null}
            {(company?.email ?? '').trim() ? (
              <div style={style}>
                E-Mail{' '}
                <a
                  href={`mailto:${(company?.email ?? '').trim()}`}
                  style={{ color: style.color, textDecoration: 'none' }}
                >
                  {(company?.email ?? '').trim()}
                </a>
              </div>
            ) : null}
          </div>
        );
      case 'pageNumber':
        return (
          <div key={el.id} style={{ ...box, ...style }}>
            Seite 1 von 1
          </div>
        );
      case 'invoiceNumber':
        return renderLabeledField(
          el,
          data.invoiceNumberLabel?.trim() || data.documentHeadingLine1.trim() || '—',
          { defaultLabel: documentNumberFieldLabel(data.documentTypeLabel) },
        );
      case 'signatureImage': {
        const src = data.assetUrls?.[el.id] ?? null;
        if (!src) return null;
        return (
          <div key={el.id} style={box}>
            <img
              src={src}
              alt="Unterschrift"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          </div>
        );
      }
      case 'logo':
        return data.logoUrl ? (
          <div key={el.id} style={box}>
            <img
              src={data.logoUrl}
              alt="Firmenlogo"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          </div>
        ) : null;
      case 'headerBanner': {
        const src = data.assetUrls?.[el.id] ?? null;
        if (!src) return null;
        return (
          <div key={el.id} style={box}>
            <img
              src={src}
              alt="Bild"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                pointerEvents: 'none',
                filter: el.imageGrayscale ? 'grayscale(100%)' : undefined,
              }}
            />
          </div>
        );
      }
      case 'freeText': {
        const segments = parseFreeTextWithTokens(el.text?.trim() || '', bandTokenValues(data));
        return (
          <div key={el.id} style={box}>
            <p style={style}>
              {segments.map((seg, i) => (
                <span
                  key={i}
                  style={{ fontWeight: seg.boldValue || el.bold ? 700 : style.fontWeight }}
                >
                  {seg.text}
                </span>
              ))}
            </p>
          </div>
        );
      }
      case 'divider':
        return (
          <div
            key={el.id}
            style={{
              ...box,
              backgroundColor: el.color || '#111827',
              ...(resolveLineOrientation(el) === 'vertical'
                ? { width: `${lineThicknessPt(el)}pt` }
                : { height: `${lineThicknessPt(el)}pt` }),
            }}
          />
        );
      case 'colorBlock':
        return (
          <div
            key={el.id}
            style={{
              ...box,
              backgroundColor: el.bg || '#eff6ff',
              borderRadius: el.radiusPt ?? 0,
              border: el.border ? '1px solid #9ca3af' : undefined,
            }}
          />
        );
      default:
        return null;
    }
  });
}

type BandProps = {
  layout: DocumentLayoutV1;
  data: PdfLayoutBandData;
  renderOverride?: HtmlBandRenderOverride;
};

export function HtmlHeaderBand({ layout, data, renderOverride }: BandProps) {
  const sorted = [...layout.header.elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${PDF_A4_WIDTH_PT}pt`,
        height: `${layout.header.heightPt}pt`,
        ...OFFER_PDF_FONT_STYLE,
      }}
    >
      {sorted.map((el) => renderHeaderElement(el, data, renderOverride))}
    </div>
  );
}

/**
 * In-flow footer band for the single continuous HTML sheet
 * (PDF footer is fixed on every page; HTML has no pagination).
 */
export function HtmlFooterBand({ layout, data, renderOverride }: BandProps) {
  const sorted = [...layout.footer.elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  return (
    <div
      style={{
        position: 'relative',
        width: `${PDF_A4_WIDTH_PT}pt`,
        height: `${layout.footer.heightPt}pt`,
        ...OFFER_PDF_FONT_STYLE,
        flexShrink: 0,
      }}
    >
      {sorted.map((el) => renderFooterElement(el, data, renderOverride))}
    </div>
  );
}

/** DIN 5008 Form B Falz-/Lochmarken mirror for the HTML document sheet. */
export function HtmlFoldMarks({ layout }: { layout: DocumentLayoutV1 }) {
  if (!layoutShowsFoldMarks(layout)) return null;
  const marks = resolveDocumentFoldMarkRects();
  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, zIndex: 4 }}>
      {marks.map((mark, index) => (
        <div
          key={`fold-mark-${mark.kind}-${index}`}
          style={{
            position: 'absolute',
            left: `${mark.xPt}pt`,
            top: `${mark.yPt}pt`,
            width: `${mark.wPt}pt`,
            height: `${mark.hPt}pt`,
            backgroundColor: DOCUMENT_FOLD_MARK_COLOR,
            pointerEvents: 'none',
          }}
        />
      ))}
    </div>
  );
}
